import { ALL_PERMISSIONS, type PermissionCode } from './permissions';

/**
 * Роли по умолчанию из раздела 4 ТЗ. Администратор меняет их состав в портале;
 * здесь — стартовый набор для новой компании.
 */
export interface DefaultRole {
  code: string;
  name: string;
  description: string;
  kind: 'internal' | 'external';
  permissions: PermissionCode[];
}

/** Матрица прав приложения В.1 ТЗ: ГИП, ПТО и планировщик в реестре контрагентов (6.12) доступа не имеют */
const NO_SUPPLIERS: PermissionCode[] = ['suppliers.view', 'suppliers.edit'];

const VIEW_ALL: PermissionCode[] = [
  'portfolio.view',
  'project.view',
  'psd.view',
  'estimate.view',
  'norms.view',
  'budget.view',
  'schedule.view',
  'resources.view',
  'procurement.view',
  'suppliers.view',
  'contracts.view',
  'site.view',
  'supervision.view',
  'changes.view',
  'warehouse.view',
  'finance.view',
  'qhse.view',
  'documents.view',
  'ai.use',
];

export const DEFAULT_ROLES: DefaultRole[] = [
  {
    code: 'full_access',
    name: 'Полный доступ',
    description:
      'Все права во всех модулях, юрлицах и проектах: для партнёров и первых руководителей. Новые права добавляются автоматически.',
    kind: 'internal',
    permissions: [...ALL_PERMISSIONS],
  },
  {
    code: 'company_head',
    name: 'Руководитель компании',
    description: 'Дашборды портфеля, закупок и денег; утверждение бюджета, изменений и закупок сверх лимита.',
    kind: 'internal',
    permissions: [
      ...VIEW_ALL,
      'analytics.view',
      'procurement.dashboard',
      'procurement.approve.limit',
      'budget.approve',
      'changes.approve',
      'estimate.approve',
      'contracts.approve.deputy',
      'roadmap.view',
    ],
  },
  {
    code: 'signatory',
    name: 'Подписант',
    description: 'Подпись договоров, допсоглашений и актов, в том числе ЭЦП.',
    kind: 'internal',
    permissions: [
      'portfolio.view',
      'project.view',
      'procurement.view',
      'contracts.view',
      'contracts.sign',
      'documents.view',
    ],
  },
  {
    code: 'project_manager',
    name: 'Руководитель проекта',
    description:
      'Бюджет и график проекта, выбор варианта исполнения, согласование заявок, КП и изменений в пределах лимита.',
    kind: 'internal',
    permissions: [
      ...VIEW_ALL,
      'analytics.view',
      'project.edit',
      'budget.edit',
      'schedule.edit',
      'procurement.request.create',
      'procurement.approve.limit',
      'procurement.dashboard',
      'contracts.edit',
      'site.review',
      'changes.request',
      'changes.approve',
      'timesheet.approve',
      'finance.edit',
      'warehouse.approve',
    ],
  },
  {
    code: 'chief_engineer',
    name: 'ГИП, ПТО',
    description:
      'Объёмы и ведомости, вопросы к проекту и извещения об изменениях, техническая приёмка КП и аналогов, исполнительная документация.',
    kind: 'internal',
    permissions: [
      ...VIEW_ALL.filter((p) => !NO_SUPPLIERS.includes(p)),
      'psd.upload',
      'psd.approve',
      'psd.stamp',
      'procurement.request.create',
      'site.review',
      'supervision.issue',
      'supervision.respond',
      'changes.request',
      'documents.edit',
    ],
  },
  {
    code: 'estimator',
    name: 'Сметчик',
    description:
      'Сметы на нормативной базе РК и их версии, KENML, ресурсная раскладка, сравнение КП со сметой.',
    kind: 'internal',
    permissions: [...VIEW_ALL, 'estimate.edit', 'norms.manage', 'budget.edit', 'changes.request'],
  },
  {
    code: 'planner',
    name: 'Планировщик',
    description: 'Календарно-сетевой график, базовые планы, ресурсы, сценарии сдвига.',
    kind: 'internal',
    permissions: [
      ...VIEW_ALL.filter((p) => !NO_SUPPLIERS.includes(p)),
      'schedule.edit',
      'schedule.baseline',
      'resources.edit',
      'changes.request',
    ],
  },
  {
    code: 'requester',
    name: 'Инициатор заявки',
    description: 'Заявка на закуп, свои заявки, доработка по возврату; стадии снабжения не двигает.',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'procurement.request.create',
      'estimate.view',
      'schedule.view',
    ],
  },
  {
    code: 'request_reviewer',
    name: 'Проверяющий заявки',
    description: 'Проверка полноты заявки; принять или вернуть с причиной.',
    kind: 'internal',
    permissions: ['project.view', 'procurement.view', 'procurement.request.review', 'estimate.view'],
  },
  {
    code: 'buyer',
    name: 'Снабженец',
    description:
      'Запросы КП, журнал контактов с поставщиками, коммерческий анализ, выбор поставщика, замена ТМЦ на аналог.',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'procurement.rfq.manage',
      'procurement.analysis',
      'suppliers.view',
      'suppliers.edit',
      'estimate.view',
      'contracts.view',
      'budget.view',
      'finance.view',
    ],
  },
  {
    code: 'contract_approver',
    name: 'Согласующий договоров',
    description:
      'Любой пункт маршрута договора и допсоглашения, кроме инициатора, — для замещения; в раунде один человек решает один пункт. Своё подразделение — роли «Юридическая служба», «Служба безопасности», «Заместитель директора» и «Финансовый отдел».',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'contracts.view',
      'contracts.approve',
      'suppliers.view',
    ],
  },
  {
    code: 'legal_counsel',
    name: 'Юридическая служба',
    description:
      'Правовая экспертиза доходных и расходных договоров и допсоглашений: пункт «Юридическая служба» в маршруте (ДГ-04).',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'contracts.view',
      'contracts.approve.legal',
      'suppliers.view',
    ],
  },
  {
    code: 'security_officer',
    name: 'Служба безопасности',
    description:
      'Проверка контрагента и рисков в маршруте договора и допсоглашения, повторная проверка при смене руководителя, участников или бенефициаров контрагента (ДГ-04, КА-25).',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'contracts.view',
      'contracts.approve.security',
      'suppliers.view',
      'suppliers.edit',
    ],
  },
  {
    code: 'deputy_director',
    name: 'Заместитель директора',
    description:
      'Условия договоров и допсоглашений в пределах полномочий: пункт «Заместитель директора» в маршруте (ДГ-04).',
    kind: 'internal',
    permissions: [...VIEW_ALL, 'analytics.view', 'procurement.dashboard', 'contracts.approve.deputy'],
  },
  {
    code: 'document_controller',
    name: 'Документолог',
    description: 'Подписание договора, оригиналы и сканы, реестр договоров.',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'contracts.view',
      'contracts.edit',
      'contracts.docs',
      'suppliers.view',
      'suppliers.edit',
      'documents.view',
      'documents.edit',
    ],
  },
  {
    code: 'registry_payments',
    name: 'Реестры и оплаты',
    description: 'Реестры закупок, заявки на оплату, номера и данные 1С, передача в финансы.',
    kind: 'internal',
    permissions: [
      'project.view',
      'procurement.view',
      'procurement.registry',
      'procurement.payment.request',
      'contracts.view',
      'suppliers.view',
      'finance.view',
    ],
  },
  {
    code: 'finance',
    name: 'Финансовый отдел',
    description: 'Стадии оплаты, факт и дата оплаты, платёжный календарь.',
    kind: 'internal',
    permissions: [
      'portfolio.view',
      'project.view',
      'procurement.view',
      'procurement.payment.request',
      'procurement.payment.confirm',
      'contracts.view',
      'contracts.approve.accounting',
      'suppliers.view',
      'finance.view',
      'finance.edit',
      'finance.manager',
      'finance.export',
      'budget.view',
      'warehouse.view',
      'warehouse.export',
    ],
  },
  {
    code: 'foreman',
    name: 'Прораб, мастер',
    description:
      'План на день, ежедневный отчёт с телефона, приёмка материала, замечания, вопросы к проекту.',
    kind: 'internal',
    permissions: [
      'project.view',
      'schedule.view',
      'psd.view',
      'site.view',
      'site.report',
      'supervision.view',
      'supervision.respond',
      'procurement.view',
      'procurement.request.create',
      'qhse.view',
      'suppliers.view',
      'suppliers.edit',
    ],
  },
  {
    code: 'storekeeper',
    name: 'Кладовщик',
    description: 'Приход, выдача, перемещение, инвентаризация.',
    kind: 'internal',
    permissions: [
      'project.view',
      'warehouse.view',
      'warehouse.operate',
      'warehouse.export',
      'procurement.view',
      'suppliers.view',
      'suppliers.edit',
    ],
  },
  {
    code: 'qhse_engineer',
    name: 'ОТК, ОТ и ПБ',
    description: 'Проверки, предписания, акты скрытых работ, наряды-допуски, инструктажи.',
    kind: 'internal',
    permissions: [
      'project.view',
      'site.view',
      'psd.view',
      'supervision.view',
      'supervision.issue',
      'qhse.view',
      'qhse.edit',
      'suppliers.view',
    ],
  },
  {
    code: 'supplier',
    name: 'Поставщик (внешний)',
    description: 'Ответ на запрос КП по форме, документы, статус отгрузки — только свои.',
    kind: 'external',
    // procurement.view — кабинет: свои приглашения на КП (ВК-06, ЗК-18); потолок — EXTERNAL_PERMISSIONS
    permissions: ['procurement.view'],
  },
  {
    code: 'subcontractor',
    name: 'Субподрядчик (внешний)',
    description:
      'КП по разделам сметы, свой график, ежедневные отчёты, акты, предписания к себе и ответы на них.',
    kind: 'external',
    // psd.view — после связи участника с контрагентом (ВК-02): иначе подрядчик видел бы всю ПСД проекта
    permissions: ['site.report', 'supervision.view', 'supervision.respond'],
  },
  {
    code: 'customer',
    name: 'Заказчик (внешний)',
    description:
      'График, фотоотчёты и отчёты по проекту, согласование изменений и допсоглашений, приёмка этапов.',
    kind: 'external',
    permissions: ['project.view', 'schedule.view', 'site.view', 'changes.view'],
  },
  {
    code: 'tech_supervision',
    name: 'Технадзор заказчика (внешний)',
    description: 'Предписания, освидетельствование скрытых работ, подтверждение объёмов, входной контроль.',
    kind: 'external',
    permissions: ['project.view', 'schedule.view', 'site.view', 'supervision.view', 'supervision.issue'],
  },
  {
    code: 'designer',
    name: 'Проектировщик и авторский надзор (внешние)',
    description: 'Ответы на вопросы к проекту, ревизии ПСД, журнал и замечания авторского надзора.',
    kind: 'external',
    permissions: [
      'project.view',
      'psd.view',
      'psd.upload',
      'supervision.view',
      'supervision.issue',
      'supervision.respond',
    ],
  },
  {
    code: 'admin',
    name: 'Администратор',
    description: 'Пользователи, роли, маршруты, справочники, интеграции.',
    kind: 'internal',
    permissions: [...ALL_PERMISSIONS],
  },
];

/**
 * Права, появившиеся после первых выкладок (25.10.2026): видео, база знаний, поддержка, отчёты, списки, персональные
 * данные (ДК-11). Роль получает новое право, если у неё есть права, по которым раньше работал этот доступ, — так
 * никто ничего не теряет. Та же логика — в миграции, которая дописывает права ролям существующих компаний.
 */
export function carriedPermissions(role: {
  kind: 'internal' | 'external';
  permissions: readonly string[];
}): PermissionCode[] {
  const p = new Set(role.permissions);
  const out: PermissionCode[] = [];
  const add = (cond: boolean, ...codes: PermissionCode[]) => {
    if (cond) for (const c of codes) if (!p.has(c)) out.push(c);
  };
  const internal = role.kind === 'internal';
  // Видео — внутренний раздел (requireVideo): внешним ролям права видео не переносятся
  add(internal && p.has('site.view') && p.has('qhse.view'), 'video.view');
  add(internal && p.has('qhse.edit'), 'video.decide');
  add(p.has('qhse.edit'), 'qhse.personal');
  add(internal && p.has('project.edit'), 'video.manage');
  add(internal && p.has('admin.settings'), 'video.biometrics');
  add(p.has('admin.settings'), 'support.handle', 'support.triage', 'kb.publish');
  add(internal && p.has('admin.audit'), 'video.audit');
  add(internal, 'kb.view', 'lists.view');
  add(p.has('documents.edit'), 'kb.edit');
  add(p.has('analytics.view'), 'analytics.report.edit', 'analytics.report.export');
  return [...new Set(out)];
}

for (const r of DEFAULT_ROLES) r.permissions.push(...carriedPermissions(r));
// Инженер ОТ и ПБ смотрит камеры и решает по событиям, даже без права на отчёты площадки (поток 8)
const qhse = DEFAULT_ROLES.find((r) => r.code === 'qhse_engineer');
if (qhse && !qhse.permissions.includes('video.view')) qhse.permissions.push('video.view');
